import type { TranscriptionLanguage, TranscriptionTargetType } from '@treams/treams-network';

const STORAGE_PREFIX = 'treams.streamingTranscription.';

export type QueuedChunk = {
	id: string;
	/** The AssemblyAI session that produced the text. */
	sessionID: string;
	text: string;
	/** The first chunk of a session, which the API precedes with a <BREAK> line. */
	newSession: boolean;
};

/**
 * `live` means a socket was open when the page last wrote this record. Seeing
 * it on load means the page closed without pausing.
 */
export type StoredSessionState = 'live' | 'paused';

export type StoredSession = {
	targetID: string;
	targetType: TranscriptionTargetType;
	targetPath: string;
	label?: string;
	transcriptionID: string | null;
	language: TranscriptionLanguage;
	state: StoredSessionState;
	elapsed: number;
	expectedLength: number;
	/** Transcript length before the session started; Cancel cuts back to it. */
	sessionBaseline?: number | null;
	/** The session created the transcription, so Cancel unlinks it. */
	createdThisSession?: boolean;
	queue: QueuedChunk[];
	updatedAt: number;
};

function storageKey(targetID: string) {
	return `${STORAGE_PREFIX}${targetID}`;
}

function parse(raw: string | null): StoredSession | null {
	if (!raw) return null;

	try {
		const value = JSON.parse(raw) as StoredSession;
		return value && typeof value.targetID === 'string' && Array.isArray(value.queue) ? value : null;
	} catch {
		return null;
	}
}

export function loadStoredSession(targetID: string): StoredSession | null {
	try {
		return parse(window.localStorage.getItem(storageKey(targetID)));
	} catch {
		return null;
	}
}

/** Every stored session, newest first. */
export function loadStoredSessions(): StoredSession[] {
	const sessions: StoredSession[] = [];

	try {
		for (let i = 0; i < window.localStorage.length; i++) {
			const key = window.localStorage.key(i);
			if (!key?.startsWith(STORAGE_PREFIX)) continue;

			const session = parse(window.localStorage.getItem(key));
			if (session) sessions.push(session);
		}
	} catch {
		return sessions;
	}

	return sessions.sort((a, b) => b.updatedAt - a.updatedAt);
}

export function saveStoredSession(session: StoredSession) {
	try {
		window.localStorage.setItem(
			storageKey(session.targetID),
			JSON.stringify({ ...session, updatedAt: Date.now() }),
		);
	} catch {
		// Storage can be full or disabled; the in-memory queue still works.
	}
}

export function removeStoredSession(targetID: string) {
	try {
		window.localStorage.removeItem(storageKey(targetID));
	} catch {
		// Ignore unavailable storage.
	}
}
